/**
 * Utility functions for rendering weborder tags with consistent styling
 */

/**
 * Get text color (black or white) based on background color for optimal contrast
 */
export function getContrastColor(hexColor: string): string {
  if (!hexColor || hexColor === '') return '#FFFFFF';
  const r = parseInt(hexColor.substr(1, 2), 16);
  const g = parseInt(hexColor.substr(3, 2), 16);
  const b = parseInt(hexColor.substr(5, 2), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

/**
 * Generate consistent color for a tag value using djb2 hash algorithm
 */
export function getTagColor(tag: string): string {
  // Predefined darker color palette for tags
  const colors = [
    '#E63946', // Red
    '#F77F00', // Orange
    '#FCBF49', // Gold
    '#06A77D', // Green
    '#0077B6', // Blue
    '#8338EC', // Purple
    '#D62828', // Crimson
    '#F4A261', // Sandy brown
    '#2A9D8F', // Teal
    '#7209B7', // Deep purple
    '#E76F51', // Burnt orange
    '#118AB2', // Cerulean
  ];
  
  // Better hash function (djb2 algorithm) for consistent color assignment with fewer collisions
  let hash = 5381;
  for (let i = 0; i < tag.length; i++) {
    hash = ((hash << 5) + hash) + tag.charCodeAt(i); // hash * 33 + c
  }
  // Ensure positive number and get index
  const index = Math.abs(hash) % colors.length;
  return colors[index];
}

/**
 * Parse weborder tags from various formats into a string array
 */
export function parseWeborderTags(tags: string[] | string | undefined | null): string[] {
  if (!tags) return [];

  // Parse tags if they're in string format
  let parsedTags: string[] = [];
  
  if (typeof tags === 'string') {
    // First try to parse as JSON array
    try {
      const parsed = JSON.parse(tags);
      if (Array.isArray(parsed)) {
        parsedTags = parsed;
      }
    } catch {
      // If not JSON, split by comma for comma-separated strings
      parsedTags = tags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0);
    }
  } else if (Array.isArray(tags)) {
    parsedTags = tags;
  }

  return parsedTags;
}

